import React, { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  ArrowLeft, Save, Loader2, CheckCircle2, Upload, Trash2,
  ArrowUp, ArrowDown, Plus, Image as ImageIcon, Timer, Coffee, Eye, EyeOff, BarChart3,
} from "lucide-react";
import { api, API, formatApiErrorDetail } from "../lib/api";
import HomeImageCard from "../components/HomeImageCard";
import CountdownCard from "../components/CountdownCard";
import BMCButtonCard from "../components/BMCButtonCard";

const PRESET_FONTS = [
  { value: "display", label: "Outfit (display)" },
  { value: "sans", label: "IBM Plex Sans" },
  { value: "serif", label: "Source Serif Pro" },
  { value: "mono", label: "IBM Plex Mono" },
];

async function uploadImage(file) {
  const fd = new FormData();
  fd.append("file", file);
  const r = await fetch(`${API}/uploads`, {
    method: "POST",
    credentials: "include",
    body: fd,
  });
  if (!r.ok) throw new Error("Upload failed");
  const data = await r.json();
  return data.url;
}

function uid() {
  // Short stable id for newly-added widgets; doesn't have to be a UUID.
  return "w_" + Math.random().toString(36).slice(2, 10);
}

function localISOToIsoUtc(local) {
  if (!local) return "";
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString();
}

function isoUtcToLocalISO(isoUtc) {
  if (!isoUtc) return "";
  const d = new Date(isoUtc);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return (
    d.getFullYear() + "-" + pad(d.getMonth() + 1) + "-" + pad(d.getDate()) +
    "T" + pad(d.getHours()) + ":" + pad(d.getMinutes())
  );
}

function newImageWidget() {
  return { id: uid(), type: "image", enabled: true, image_url: "", link_url: "", alt: "", open_in_new_tab: false };
}

function newCountdownWidget() {
  return {
    id: uid(), type: "countdown",
    enabled: true, mode: "manual", title: "", target_iso: "",
    background_image_url: "", background_opacity: 100,
    text_color: "#FFFFFF", font_family: "display",
    show_seconds: true, stroke: true,
  };
}

function newBMCWidget() {
  // Defaults match the user's own snippet so first-time-add looks
  // right immediately. Admin can change them per-instance. White
  // outline is used so the button blends with the white-strokeed
  // hero / explore tiles on the home page.
  return {
    id: uid(), type: "bmc",
    enabled: true,
    slug: "lastjonny",
    color: "#FFDD00",
    text: "Buy LAST a Batch!",
    emoji: "🍔",
    font: "Poppins",
    outline_color: "#FFFFFF",
    font_color: "#000000",
    coffee_color: "#FFFFFF",
  };
}

function newSpotifyWidget() {
  return {
    id: uid(),
    type: "spotify",
    enabled: true,
    height: 380,
  };
}

function newPollWidget() {
  return {
    id: uid(),
    type: "poll",
    enabled: true,
    poll_id: "",
  };
}

export default function AdminHomepageWidgets() {
  const [widgets, setWidgets] = useState([]);
  const [navItems, setNavItems] = useState([]);
  const [customPages, setCustomPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    Promise.all([
      api.get("/settings/homepage-right-column").then((r) => r.data),
      // Nav-items power the "internal pages" dropdown on the image
      // widget's Link URL field. Failing this fetch isn't critical —
      // the field still accepts free text — so swallow the error.
      api.get("/nav-items").then((r) => r.data).catch(() => []),
      // Custom pages NOT flagged for the header nav still need to be
      // selectable as link targets — fetch the full list and merge
      // with nav-items below.
      api.get("/custom-pages").then((r) => r.data).catch(() => []),
    ])
      .then(([rc, nav, pages]) => {
        const list = Array.isArray(rc?.widgets) ? rc.widgets : [];
        setWidgets(list.map((w) => (w.id ? w : { ...w, id: uid() })));
        setNavItems(Array.isArray(nav) ? nav : []);
        setCustomPages(Array.isArray(pages) ? pages : []);
      })
      .catch((e) => setErr(formatApiErrorDetail(e)))
      .finally(() => setLoading(false));
  }, []);

  const update = (id, patch) =>
    setWidgets((s) => s.map((w) => (w.id === id ? { ...w, ...patch } : w)));

  const remove = (id) => setWidgets((s) => s.filter((w) => w.id !== id));

  const move = (id, delta) => {
    setWidgets((s) => {
      const i = s.findIndex((w) => w.id === id);
      if (i < 0) return s;
      const j = i + delta;
      if (j < 0 || j >= s.length) return s;
      const next = s.slice();
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };

  const addImage = () => {
    setWidgets((s) => [...s, newImageWidget()]);
  };

  const addCountdown = () => {
    setWidgets((s) => [...s, newCountdownWidget()]);
  };

  const addBMC = () => {
    setWidgets((s) => [...s, newBMCWidget()]);
  };

  const addSpotify = () => {
    setWidgets((s) => [...s, newSpotifyWidget()]);
  };

  const addPoll = () => {
    setWidgets((s) => [...s, newPollWidget()]);
  };

  const save = async () => {
    setSaving(true);
    setErr("");
    try {
      // Normalise countdown target_iso to ISO UTC before sending.
      const payload = widgets.map((w) => {
        if (w.type !== "countdown") return w;
        const t = w.target_iso || "";
        const isUtcAlready = t.endsWith("Z") || /[+-]\d{2}:\d{2}$/.test(t);
        return {
          ...w,
          target_iso: t && !isUtcAlready ? localISOToIsoUtc(t) : t,
        };
      });
      await api.put("/settings/homepage-right-column", { widgets: payload });
      setSavedFlash(true);
      window.setTimeout(() => setSavedFlash(false), 2000);
    } catch (e) {
      setErr(formatApiErrorDetail(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--page-bg)] p-10 text-[var(--brand-ink)]">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--page-bg)] text-[var(--brand-ink)]">
      <main className="mx-auto max-w-5xl px-5 py-10 sm:px-8 sm:py-14">
        <RouterLink
          to="/admin"
          className="inline-flex items-center gap-1 text-sm text-[var(--brand-body)] hover:text-[var(--brand-ink)]"
          data-testid="admin-widgets-back"
        >
          <ArrowLeft className="h-4 w-4" /> Back to admin
        </RouterLink>

        <h1 className="mt-4 font-display text-3xl font-black sm:text-4xl">
          Homepage widgets
        </h1>
        <p className="mt-1 text-sm text-[var(--brand-body)]">
          Right-column widgets stack vertically next to the hero card on the
          home page. Add image cards and a countdown timer, reorder them with
          the up/down arrows, and they&apos;ll render in the order shown here.
        </p>

        {err ? (
          <div
            className="mt-4 rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700"
            data-testid="admin-widgets-error"
          >
            {err}
          </div>
        ) : null}

        {/* Widget list */}
        <div className="mt-6 space-y-4" data-testid="admin-widgets-list">
          {widgets.length === 0 ? (
            <div className="rounded-md border border-dashed border-black/15 bg-white p-6 text-sm italic text-[var(--brand-body)]">
              No widgets yet — add an image card or a countdown to get
              started.
            </div>
          ) : null}

          {widgets.map((w, i) => (
            <WidgetCard
              key={w.id}
              widget={w}
              index={i}
              total={widgets.length}
              navItems={navItems}
              customPages={customPages}
              onChange={(patch) => update(w.id, patch)}
              onRemove={() => remove(w.id)}
              onMoveUp={() => move(w.id, -1)}
              onMoveDown={() => move(w.id, +1)}
            />
          ))}
        </div>

        {/* Add buttons */}
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={addImage}
            disabled={false}
            className="inline-flex items-center gap-2 rounded-md border border-black/15 bg-white px-3 py-2 text-sm font-semibold hover:border-[#0A0F17] disabled:opacity-60"
            data-testid="admin-widgets-add-image"
          >
            <Plus className="h-4 w-4" /> <ImageIcon className="h-4 w-4" /> Add image card
          </button>
          <button
            type="button"
            onClick={addCountdown}
            disabled={widgets.some((w) => w.type === "countdown")}
            className="inline-flex items-center gap-2 rounded-md border border-black/15 bg-white px-3 py-2 text-sm font-semibold hover:border-[#0A0F17] disabled:opacity-60"
            data-testid="admin-widgets-add-countdown"
            title={widgets.some((w) => w.type === "countdown") ? "Only one countdown supported" : ""}
          >
            <Plus className="h-4 w-4" /> <Timer className="h-4 w-4" /> Add countdown
          </button>
          <button
            type="button"
            onClick={addBMC}
            disabled={false}
            className="inline-flex items-center gap-2 rounded-md border border-black/15 bg-white px-3 py-2 text-sm font-semibold hover:border-[#0A0F17] disabled:opacity-60"
            data-testid="admin-widgets-add-bmc"
          >
            <Plus className="h-4 w-4" /> <Coffee className="h-4 w-4" /> Add Buy Me a Coffee
          </button>
          <button
            type="button"
            onClick={addSpotify}
            disabled={widgets.some((w) => w.type === "spotify")}
            className="inline-flex items-center gap-2 rounded-md border border-black/15 bg-white px-3 py-2 text-sm font-semibold hover:border-[#0A0F17] disabled:opacity-60"
            data-testid="admin-widgets-add-spotify"
            title={widgets.some((w) => w.type === "spotify") ? "Only one Spotify widget supported" : ""}
          >
            <Plus className="h-4 w-4" /> <span className="font-display font-bold">🎵</span> Add Spotify Playlist
          </button>
          <button
            type="button"
            onClick={addPoll}
            disabled={widgets.some((w) => w.type === "poll")}
            className="inline-flex items-center gap-2 rounded-md border border-black/15 bg-white px-3 py-2 text-sm font-semibold hover:border-[#0A0F17] disabled:opacity-60"
            data-testid="admin-widgets-add-poll"
            title={widgets.some((w) => w.type === "poll") ? "Only one Poll widget supported" : ""}
          >
            <Plus className="h-4 w-4" /> <BarChart3 className="h-4 w-4" /> Add Featured Poll
          </button>
        </div>

        {/* Save bar */}
        <div className="mt-8 flex items-center gap-3">
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-md bg-[#0A0F17] px-4 py-2 font-display text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"
            data-testid="admin-widgets-save"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            Save changes
          </button>
          {savedFlash ? (
            <span
              className="inline-flex items-center gap-1 text-sm text-emerald-700"
              data-testid="admin-widgets-saved-flash"
            >
              <CheckCircle2 className="h-4 w-4" /> Saved
            </span>
          ) : null}
        </div>
      </main>
    </div>
  );
}

function WidgetCard({ widget, index, total, navItems, customPages, onChange, onRemove, onMoveUp, onMoveDown }) {
  const isImage = widget.type === "image";
  const isCountdown = widget.type === "countdown";
  const isBMC = widget.type === "bmc";
  const isSpotify = widget.type === "spotify";
  const isPoll = widget.type === "poll";
  let icon = null;
  let label = "";
  if (isImage) { icon = <ImageIcon className="h-4 w-4" />; label = "Image card"; }
  else if (isCountdown) { icon = <Timer className="h-4 w-4" />; label = "Countdown"; }
  else if (isBMC) { icon = <Coffee className="h-4 w-4" />; label = "Buy Me a Coffee"; }
  else if (isSpotify) { icon = <span className="font-display font-bold text-sm">🎵</span>; label = "Spotify Playlist"; }
  else if (isPoll) { icon = <BarChart3 className="h-4 w-4" />; label = "Featured Poll"; }

  // Treat undefined as enabled so existing saved widgets keep showing
  // after the field was introduced.
  const enabled = widget.enabled !== false;

  return (
    <div
      className={`rounded-md border bg-white p-4 ${enabled ? "border-black/10" : "border-dashed border-black/20 opacity-70"}`}
      data-testid={`admin-widget-card-${index}`}
      data-widget-type={widget.type}
      data-widget-enabled={enabled ? "true" : "false"}
    >
      {/* Header row */}
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-semibold">
          {icon}
          {label}
          <span className="text-xs font-normal text-[var(--brand-body)]">#{index + 1}</span>
          {!enabled ? (
            <span className="rounded-sm bg-black/5 px-1.5 py-0.5 label-kicker text-[var(--brand-body)]">
              Hidden
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onChange({ enabled: !enabled })}
            className={`mr-1 inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-semibold ${
              enabled
                ? "border-black/10 text-[var(--brand-body)] hover:border-[#0A0F17] hover:text-[var(--brand-ink)]"
                : "border-[#6CABDD] bg-[#6CABDD]/10 text-[var(--brand-ink)] hover:bg-[#6CABDD]/20"
            }`}
            title={enabled ? "Hide this widget on the home page" : "Show this widget on the home page"}
            data-testid={`admin-widget-enabled-${index}`}
          >
            {enabled ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
            {enabled ? "Visible" : "Hidden"}
          </button>
          <button
            type="button"
            onClick={onMoveUp}
            disabled={index === 0}
            className="rounded p-1 hover:bg-black/5 disabled:opacity-30"
            title="Move up"
            data-testid={`admin-widget-up-${index}`}
          >
            <ArrowUp className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={onMoveDown}
            disabled={index === total - 1}
            className="rounded p-1 hover:bg-black/5 disabled:opacity-30"
            title="Move down"
            data-testid={`admin-widget-down-${index}`}
          >
            <ArrowDown className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={onRemove}
            className="rounded p-1 text-red-600 hover:bg-red-50"
            title="Delete widget"
            data-testid={`admin-widget-remove-${index}`}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {isImage ? <ImageEditor widget={widget} navItems={navItems} customPages={customPages} onChange={onChange} /> : null}
      {isCountdown ? <CountdownEditor widget={widget} onChange={onChange} /> : null}
      {isBMC ? <BMCEditor widget={widget} onChange={onChange} /> : null}
      {isSpotify ? <SpotifyEditor widget={widget} onChange={onChange} /> : null}
      {isPoll ? <PollEditor widget={widget} onChange={onChange} /> : null}
    </div>
  );
}

function ImageEditor({ widget, navItems, customPages, onChange }) {
  const [uploading, setUploading] = useState(false);
  const onFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadImage(file);
      onChange({ image_url: url });
    } finally {
      setUploading(false);
    }
  };

  // Internal-page choices for the dropdown:
  //   - "Home" (root)
  //   - Every enabled item from /api/nav-items
  //   - Every custom page from /api/custom-pages (whether or not it's
  //     flagged for the header nav), since the admin may want to link
  //     to pages that aren't navigable from the top bar.
  // Deduped by `to`, sorted alphabetically by label so they're easy to
  // find in the dropdown.
  const internalOptions = React.useMemo(() => {
    const opts = [{ to: "/", label: "Home" }];
    (navItems || []).forEach((n) => {
      if (n && n.to && n.to.startsWith("/")) {
        opts.push({ to: n.to, label: n.label || n.to });
      }
    });
    (customPages || []).forEach((p) => {
      if (p && p.slug) {
        opts.push({
          to: `/page/${p.slug}`,
          label: p.title || p.nav_label || p.slug,
        });
      }
    });
    const seen = new Set();
    return opts
      .filter((o) => {
        if (seen.has(o.to)) return false;
        seen.add(o.to);
        return true;
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [navItems, customPages]);

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div className="space-y-3">
        <Field label="Image URL">
          <input
            type="text"
            value={widget.image_url || ""}
            onChange={(e) => onChange({ image_url: e.target.value })}
            placeholder="/api/files/... or https://"
            className="block w-full rounded-sm border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A0F17]"
          />
          <label className="mt-2 inline-flex cursor-pointer items-center gap-2 rounded-md border border-black/15 px-3 py-1.5 text-xs font-semibold hover:border-[#0A0F17]">
            {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
            Upload image
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => onFile(e.target.files && e.target.files[0])}
            />
          </label>
          {widget.image_url ? (
            <button
              type="button"
              onClick={() => onChange({ image_url: "" })}
              className="ml-2 inline-flex items-center gap-1 text-xs text-red-600 hover:underline"
            >
              <Trash2 className="h-3 w-3" /> Clear
            </button>
          ) : null}
        </Field>
        <Field label="Link URL">
          <input
            type="text"
            value={widget.link_url || ""}
            onChange={(e) => onChange({ link_url: e.target.value })}
            placeholder="https://... or /custom-page"
            className="block w-full rounded-sm border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A0F17]"
            data-testid={`admin-widget-link-${widget.id}`}
          />
          {/* Internal-page picker — populates the input. The text input
              above still accepts arbitrary external URLs. */}
          <div className="mt-2 flex items-center gap-2 text-xs">
            <label className="text-[var(--brand-body)]" htmlFor={`link-pick-${widget.id}`}>
              Or pick an internal page:
            </label>
            <select
              id={`link-pick-${widget.id}`}
              value=""
              onChange={(e) => {
                if (e.target.value) onChange({ link_url: e.target.value });
              }}
              className="rounded-sm border border-black/15 bg-white px-2 py-1 text-xs outline-none focus:border-[#0A0F17]"
              data-testid={`admin-widget-link-picker-${widget.id}`}
            >
              <option value="">— choose —</option>
              {internalOptions.map((o) => (
                <option key={o.to} value={o.to}>
                  {o.label}  ({o.to})
                </option>
              ))}
            </select>
          </div>
          <p className="text-xs italic text-[var(--brand-body)]">Internal pages open in the same tab; external links open in a new tab by default. Toggle to override.</p>
        </Field>
        <Field label="Link behaviour">
          <label className="inline-flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={!!widget.open_in_new_tab}
              onChange={(e) => onChange({ open_in_new_tab: e.target.checked })}
              data-testid={`admin-widget-new-tab-${widget.id}`}
            />
            <span>Open this link in a new tab/window</span>
          </label>
        </Field>
        <Field label="Alt text">
          <input
            type="text"
            value={widget.alt || ""}
            onChange={(e) => onChange({ alt: e.target.value })}
            placeholder="Describes the image for screen readers"
            className="block w-full rounded-sm border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A0F17]"
          />
        </Field>
      </div>
      <div>
        <p className="label-kicker text-[var(--brand-body)]">Preview</p>
        <div className="mt-2">
          <HomeImageCard
            image={{
              image_url: widget.image_url,
              link_url: widget.link_url,
              alt: widget.alt,
              open_in_new_tab: widget.open_in_new_tab,
            }}
          />
          {!widget.image_url ? (
            <p className="text-xs italic text-[var(--brand-body)]">
              (No image set — widget will be hidden on the home page.)
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function CountdownEditor({ widget, onChange }) {
  const [uploadingBg, setUploadingBg] = useState(false);
  const onBgFile = async (file) => {
    if (!file) return;
    setUploadingBg(true);
    try {
      const url = await uploadImage(file);
      onChange({ background_image_url: url });
    } finally {
      setUploadingBg(false);
    }
  };
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div className="space-y-3">
        <Field label="Mode">
          <select
            value={widget.mode || "manual"}
            onChange={(e) => onChange({ mode: e.target.value })}
            className="block w-full rounded-sm border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A0F17]"
          >
            <option value="manual">Manual — I&apos;ll set a date &amp; time</option>
            <option value="next_fixture">Auto — next Coventry fixture</option>
          </select>
        </Field>
        <Field label="Title / kicker">
          <input
            type="text"
            value={widget.title || ""}
            onChange={(e) => onChange({ title: e.target.value })}
            placeholder='e.g. "Countdown to next Premier League game"'
            className="block w-full rounded-sm border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A0F17]"
          />
        </Field>
        {widget.mode === "manual" ? (
          <Field label="Target date & time (your local timezone)">
            <input
              type="datetime-local"
              value={isoUtcToLocalISO(widget.target_iso || "")}
              onChange={(e) => onChange({ target_iso: localISOToIsoUtc(e.target.value) })}
              className="block w-full rounded-sm border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A0F17]"
            />
          </Field>
        ) : (
          <p className="rounded-sm border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900">
            Target auto-pulls from football-data.org.
          </p>
        )}
        <Field label="Background image (optional)">
          <input
            type="text"
            value={widget.background_image_url || ""}
            onChange={(e) => onChange({ background_image_url: e.target.value })}
            placeholder="/api/files/... or https://"
            className="block w-full rounded-sm border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A0F17]"
          />
          <label className="mt-2 inline-flex cursor-pointer items-center gap-2 rounded-md border border-black/15 px-3 py-1.5 text-xs font-semibold hover:border-[#0A0F17]">
            {uploadingBg ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
            Upload background
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => onBgFile(e.target.files && e.target.files[0])}
            />
          </label>
        </Field>
        {widget.background_image_url ? (
          <Field label={`Background image opacity (${widget.background_opacity ?? 100}%)`}>
            <input
              type="range"
              min={0}
              max={100}
              step={5}
              value={widget.background_opacity ?? 100}
              onChange={(e) => onChange({ background_opacity: Number(e.target.value) })}
              className="w-full"
              data-testid={`admin-widget-bg-opacity-${widget.id}`}
            />
            <p className="mt-1 text-xs text-[var(--brand-body)]">
              100% = full image · lower values fade the image so the navy
              fallback colour shows through (the dark legibility overlay
              fades with it so the timer text stays readable).
            </p>
          </Field>
        ) : null}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Text colour">
            <input
              type="color"
              value={widget.text_color || "#FFFFFF"}
              onChange={(e) => onChange({ text_color: e.target.value })}
              className="block h-9 w-full cursor-pointer rounded-sm border border-black/15 bg-white"
            />
          </Field>
          <Field label="Font">
            <input
              list={`cd-fonts-${widget.id}`}
              type="text"
              value={widget.font_family || "display"}
              onChange={(e) => onChange({ font_family: e.target.value })}
              placeholder='Type any Google Font (e.g. "Bebas Neue")'
              className="block w-full rounded-sm border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A0F17]"
            />
            <datalist id={`cd-fonts-${widget.id}`}>
              {PRESET_FONTS.map((f) => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </datalist>
          </Field>
        </div>
        <div className="flex items-center gap-4">
          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={widget.show_seconds !== false}
              onChange={(e) => onChange({ show_seconds: e.target.checked })}
            />
            Show seconds
          </label>
          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={!!widget.stroke}
              onChange={(e) => onChange({ stroke: e.target.checked })}
              data-testid={`admin-widget-stroke-${widget.id}`}
            />
            White outline
          </label>
        </div>
      </div>
      <div>
        <p className="label-kicker text-[var(--brand-body)]">Preview</p>
        <div className="mt-2">
          <CountdownCard config={{ ...widget, enabled: true }} />
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wide text-[var(--brand-body)]">
        {label}
      </label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function BMCEditor({ widget, onChange }) {
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div className="space-y-3">
        <Field label="BMC slug (the bit after buymeacoffee.com/)">
          <input
            type="text"
            value={widget.slug || ""}
            onChange={(e) => onChange({ slug: e.target.value.trim() })}
            placeholder="lastjonny"
            className="block w-full rounded-sm border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A0F17]"
            data-testid={`admin-widget-bmc-slug-${widget.id}`}
          />
        </Field>
        <Field label="Button text">
          <input
            type="text"
            value={widget.text || ""}
            onChange={(e) => onChange({ text: e.target.value })}
            placeholder="Buy LAST a Batch!"
            className="block w-full rounded-sm border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A0F17]"
          />
        </Field>
        <Field label="Emoji">
          <input
            type="text"
            value={widget.emoji || ""}
            onChange={(e) => onChange({ emoji: e.target.value })}
            placeholder="🍔"
            maxLength={4}
            className="block w-20 rounded-sm border border-black/15 bg-white px-3 py-2 text-center text-base outline-none focus:border-[#0A0F17]"
          />
        </Field>
        <Field label="Font">
          <select
            value={widget.font || "Poppins"}
            onChange={(e) => onChange({ font: e.target.value })}
            className="block w-full rounded-sm border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A0F17]"
          >
            <option value="Poppins">Poppins (BMC default)</option>
            <option value="Lato">Lato</option>
            <option value="Inter">Inter</option>
            <option value="Cookie">Cookie</option>
            <option value="Arial">Arial</option>
          </select>
        </Field>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Background">
            <input
              type="color"
              value={widget.color || "#FFDD00"}
              onChange={(e) => onChange({ color: e.target.value })}
              className="block h-9 w-full cursor-pointer rounded-sm border border-black/15 bg-white"
            />
          </Field>
          <Field label="Text">
            <input
              type="color"
              value={widget.font_color || "#000000"}
              onChange={(e) => onChange({ font_color: e.target.value })}
              className="block h-9 w-full cursor-pointer rounded-sm border border-black/15 bg-white"
            />
          </Field>
          <Field label="Outline">
            <input
              type="color"
              value={widget.outline_color || "#000000"}
              onChange={(e) => onChange({ outline_color: e.target.value })}
              className="block h-9 w-full cursor-pointer rounded-sm border border-black/15 bg-white"
            />
          </Field>
        </div>
        <Field label="Emoji background colour">
          <input
            type="color"
            value={widget.coffee_color || "#FFFFFF"}
            onChange={(e) => onChange({ coffee_color: e.target.value })}
            className="block h-9 w-20 cursor-pointer rounded-sm border border-black/15 bg-white"
          />
        </Field>
      </div>
      <div>
        <p className="label-kicker text-[var(--brand-body)]">Preview</p>
        <div className="mt-2">
          <BMCButtonCard config={widget} />
        </div>
        <p className="mt-2 text-xs italic text-[var(--brand-body)]">
          Opens https://www.buymeacoffee.com/{widget.slug || "<slug>"} in a new tab.
        </p>
      </div>
    </div>
  );
}

function SpotifyEditor({ widget, onChange }) {
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div className="space-y-3">
        <p className="text-sm text-[var(--brand-body)]">
          This widget automatically embeds and shows your active Spotify playlist from the{" "}
          <RouterLink to="/admin/spotify" className="underline font-bold text-[#6CABDD]">
            Spotify Settings page
          </RouterLink>.
        </p>
        <Field label="Widget height (px)">
          <input
            type="number"
            min={200}
            max={800}
            step={20}
            value={widget.height ?? 380}
            onChange={(e) => onChange({ height: Number(e.target.value) || 380 })}
            className="block w-32 rounded-sm border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A0F17]"
          />
          <p className="mt-1 text-xs text-[var(--brand-body)]">
            Adjust the height of the embedded Spotify player frame (default is 380px).
          </p>
        </Field>
      </div>
      <div>
        <p className="label-kicker text-[var(--brand-body)]">Preview</p>
        <div className="mt-2 rounded-md border border-black/10 p-3 bg-[#F8FAFC]">
          <p className="text-xs italic text-[var(--brand-body)]">
            (Embedded Spotify frame of {widget.height ?? 380}px height will display here on the homepage.)
          </p>
        </div>
      </div>
    </div>
  );
}

function PollEditor({ widget, onChange }) {
  const [polls, setPolls] = useState([]);
  useEffect(() => {
    api.get("/polls").then((r) => setPolls(r.data || [])).catch(() => {});
  }, []);

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div className="space-y-3">
        <p className="text-sm text-[var(--brand-body)]">
          Select an active fan poll to feature as a dynamic voting widget in your homepage sidebar.
        </p>
        <Field label="Choose Featured Poll">
          <select
            value={widget.poll_id || ""}
            onChange={(e) => onChange({ poll_id: e.target.value })}
            className="mt-1 block w-full rounded-md border border-black/15 bg-white px-3 py-2 text-sm text-[var(--brand-ink)] focus:border-[#6CABDD] focus:outline-none"
          >
            <option value="">-- Select Poll --</option>
            {polls.map((p) => (
              <option key={p.id} value={p.id}>
                {p.question} {p.closed ? "(Closed)" : ""}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <div>
        <p className="label-kicker text-[var(--brand-body)]">Preview</p>
        <div className="mt-2 rounded-md border border-black/10 p-4 bg-[#F8FAFC]">
          {widget.poll_id ? (
            <p className="text-xs italic text-emerald-600 font-bold">
              ✓ Featured Poll (ID: {widget.poll_id}) is linked and will render live in the sidebar.
            </p>
          ) : (
            <p className="text-xs italic text-[var(--brand-body)]">
              (No poll selected yet. Please choose a poll from the dropdown list.)
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
